import React from 'react';

import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';

import type { ZuubOnboardingStatusResponse } from '@/@types/zuub/contracts';

/**
 * Shared helper function to get consistent status chip props for payer connection status.
 * This ensures the sidebar and main page always show the same status.
 *
 * @param statusData - The status response from the API, or undefined if not available
 * @param isActivelyTesting - Whether the payer is currently being tested (takes priority)
 * @returns Status chip props with text, variant, and optional icon
 */
export const getPayerStatusChipProps = (
  statusData: ZuubOnboardingStatusResponse | undefined,
  isActivelyTesting: boolean = false,
): {
  text: React.ReactNode;
  variant: 'success' | 'error' | 'warning' | 'progress' | 'default';
  icon?: React.ReactNode;
} => {
  // Priority 1: If actively testing, always show "Connecting" (takes priority over status)
  if (isActivelyTesting) {
    return { text: <Trans>Connecting</Trans>, variant: 'progress' as const };
  }

  // Priority 2: If no status data, show "Not connected"
  if (!statusData) {
    return {
      text: <Trans>Not connected</Trans>,
      variant: 'default' as const,
      icon: <FontAwesomeIcon icon={faCircleXmark} />,
    };
  }

  // Priority 3: Check onboardingStatus first - if null, show "Not connected"
  if (statusData.onboardingStatus == null) {
    return {
      text: <Trans>Not connected</Trans>,
      variant: 'default' as const,
      icon: <FontAwesomeIcon icon={faCircleXmark} />,
    };
  }

  // Priority 4: Proceed with credentialStatus logic
  const status = statusData.credentialStatus;

  // Priority 5: Succeeded status
  if (status === 'Succeeded') {
    return { text: <Trans>Connected</Trans>, variant: 'success' as const };
  }

  // Priority 6: Failed status
  if (status === 'Failed') {
    return {
      text: <Trans>Connection Error</Trans>,
      variant: 'error' as const,
    };
  }

  // Priority 7: In-progress statuses (Acknowledged or InProgress)
  if (status === 'Acknowledged' || status === 'InProgress') {
    return { text: <Trans>Connecting</Trans>, variant: 'progress' as const };
  }

  // Priority 8: Default state (including null credentialStatus)
  return {
    text: <Trans>Not connected</Trans>,
    variant: 'default' as const,
    icon: <FontAwesomeIcon icon={faCircleXmark} />,
  };
};

/**
 * Type for status chip props returned by getPayerStatusChipProps
 */
export type StatusChipProps = ReturnType<typeof getPayerStatusChipProps>;

/**
 * Formats a phone number for US/CA numbers as (XXX) XXX-XXXX
 *
 * @param phoneNumber - The phone number to format (can include non-digit characters)
 * @returns Formatted phone number string, or empty string if input is empty
 */
export const formatPhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
    )}`;
  }

  // Format as +1 (XXX) XXX-XXXX for 11 digits starting with 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(
      7,
    )}`;
  }

  // Return original if it doesn't match expected patterns
  return phoneNumber;
};
